import { supabase } from '../supabase';
import type { Customer } from '../customers';
import { triggerEnterStageAutomations } from '../automationsV2';

/** Event fired after a lead is created so open conversation views can refresh. */
export const LEAD_CREATED_EVENT = 'crm:lead-created';

export function emitLeadCreated(customerId: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent(LEAD_CREATED_EVENT, { detail: { customerId } }),
    );
  } catch {
    // no-op (non-browser env)
  }
}

export type CreateLeadOptions = {
  source?: string | null;
  lead_info?: string | null;
  title?: string | null;
  /** Explicit pipeline; defaults to the business's first pipeline. */
  pipeline_id?: number | null;
  /** Explicit stage within the pipeline; defaults to its first stage. */
  stage_id?: number | null;
};

export type LeadCreator = {
  /** Auth user creating the lead (null when unauthenticated, e.g. public form). */
  userId: string | null;
  /** That user's linked resource in this business (null = admin with no resource). */
  resourceId: string | null;
};

/**
 * Who is creating a lead right now: the signed-in auth user plus their linked
 * resource in the business (if any). Used to stamp created_by fields and to
 * default the lead's resource assignment to the creator.
 */
export async function resolveLeadCreator(businessId: string): Promise<LeadCreator> {
  const empty: LeadCreator = { userId: null, resourceId: null };
  if (!supabase || !businessId) return empty;
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id ?? null;
    if (!userId) return empty;

    const { data: resources } = await supabase
      .from('resources')
      .select('id')
      .eq('business_id', businessId)
      .eq('auth_user_id', userId)
      .limit(1);

    return { userId, resourceId: (resources?.[0]?.id as string | undefined) ?? null };
  } catch (err) {
    console.error('resolveLeadCreator failed:', err);
    return empty;
  }
}

/**
 * Creates a new open CRM lead (opportunity) for a customer. Uses the given
 * pipeline/stage when provided, otherwise the business's first pipeline +
 * first stage. No UI — the caller decides how to surface it.
 * Returns the new opportunity id.
 */
export async function createLeadForCustomer(
  businessId: string,
  customer: Pick<Customer, 'id' | 'first_name' | 'last_name'>,
  options?: CreateLeadOptions,
): Promise<number> {
  if (!supabase || !businessId || !customer?.id) {
    throw new Error('createLeadForCustomer: missing required parameters');
  }

  // Explicit pipeline when given; otherwise the business's first pipeline
  // (same ordering as the CRM page).
  let pipelineQuery = supabase
    .from('pipelines')
    .select('id, stages')
    .eq('business_id', businessId);
  pipelineQuery = options?.pipeline_id != null
    ? pipelineQuery.eq('id', options.pipeline_id)
    : pipelineQuery
        .order('sort_order', { ascending: true })
        .order('pipeline_name', { ascending: true });
  const { data: pipelines, error: pipelineError } = await pipelineQuery.limit(1);

  if (pipelineError) throw pipelineError;

  const pipeline = pipelines?.[0];
  if (!pipeline) throw new Error('no-pipeline');

  const stages = Array.isArray(pipeline.stages) ? pipeline.stages : [];
  const firstStage =
    (options?.stage_id != null
      ? stages.find((s: { id?: number }) => s?.id === options.stage_id)
      : undefined) ?? stages[0];
  if (!firstStage) throw new Error('no-stage');

  const title =
    (options?.title || '').trim() ||
    `${customer.first_name || ''} ${customer.last_name || ''}`.trim() ||
    'New Lead';

  // Stamp who created the lead; the creator's resource is also the default assignee.
  const creator = await resolveLeadCreator(businessId);

  const insertPayload: Record<string, unknown> = {
    business_id: businessId,
    customer_id: customer.id,
    title,
    pipeline_id: pipeline.id,
    stage_id: firstStage.id,
    value: 0,
    status: 'Open',
    created_by_user_id: creator.userId,
    created_by_resource_id: creator.resourceId,
    assigned_resource_id: creator.resourceId,
  };

  if (options?.source != null && String(options.source).trim()) {
    insertPayload.source = String(options.source).trim();
  }
  if (options?.lead_info != null && String(options.lead_info).trim()) {
    insertPayload.lead_info = String(options.lead_info).trim();
  }

  const { data, error } = await supabase
    .from('pipeline_opportunities')
    .insert(insertPayload)
    .select('id')
    .single();

  if (error) throw error;

  // Fire entry automations (best-effort; mirrors the CRM create flow).
  void triggerEnterStageAutomations(businessId, data.id, { previousStageId: null });

  return data.id as number;
}
